import {forwardRef, InputHTMLAttributes} from 'react';
import styled from 'styled-components';

import Luiv3Constants, {LuiV3ColorKey} from './constants';
import FaIcon from './FaIcon';

type Size = 'small' | 'default';

interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  color?: LuiV3ColorKey;
  size?: Size;
}

const sizes: Record<Size, string> = {
  small: '0.85rem',
  default: '1rem',
};

const HiddenInput = styled.input.attrs({type: 'checkbox'})`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CheckedIcon = styled(FaIcon)<{$color: LuiV3ColorKey; $size: Size}>`
  display: none;
  color: ${({$color}) => Luiv3Constants.baseColor[$color]};
  width: ${({$size}) => sizes[$size]};
  height: ${({$size}) => sizes[$size]};

  ${HiddenInput}:checked ~ & {
    display: block;
  }

  ${HiddenInput}:disabled:checked ~ & {
    opacity: 0.5;
  }
`;

const UncheckedIcon = styled(FaIcon)<{$size: Size}>`
  display: block;
  width: ${({$size}) => sizes[$size]};
  color: ${Luiv3Constants.baseColor['gray-400']};
  height: ${({$size}) => sizes[$size]};

  ${HiddenInput}:checked ~ & {
    display: none;
  }

  ${HiddenInput}:disabled:not(:checked) ~ & {
    opacity: 0.5;
  }
`;

const Wrapper = styled.div<{$size: Size}>`
  display: inline-flex;
  position: relative;
  cursor: pointer;

  font-size: ${({$size}) => sizes[$size]};

  &:has(${HiddenInput}:disabled) {
    cursor: not-allowed;
  }

  &:has(${HiddenInput}:not(:disabled):checked):hover ${CheckedIcon} {
    color: ${Luiv3Constants.baseColor['blue-800']};
  }

  &:has(${HiddenInput}:not(:disabled):not(:checked)):hover ${UncheckedIcon} {
    color: ${Luiv3Constants.baseColor['gray-700']};
  }

  // This handles lui2 input label wrapper & hovering over the label text
  label:hover &:has(${HiddenInput}:not(:disabled):checked) ${CheckedIcon} {
    color: ${Luiv3Constants.baseColor['blue-800']};
  }

  label:hover
    &:has(${HiddenInput}:not(:disabled):not(:checked))
    ${UncheckedIcon} {
    color: ${Luiv3Constants.baseColor['gray-700']};
  }

  label:has(&) {
    cursor: pointer;
  }

  label:has(& ${HiddenInput}:disabled) {
    cursor: not-allowed;
  }

  &:has(input:focus-visible) {
    outline: 2px solid ${Luiv3Constants.baseColor['blue-200']};
    outline-offset: 2px;
    border-radius: 0.25rem;
  }
`;

const Checkbox = forwardRef<HTMLInputElement, Props>(
  ({size = 'default', color = 'blue-600', ...inputProps}, ref) => {
    return (
      <Wrapper $size={size}>
        <HiddenInput ref={ref} {...inputProps} />
        <CheckedIcon
          icon="square-check"
          faStyle="solid"
          $color={color}
          $size={size}
        />
        <UncheckedIcon icon="square" faStyle="regular" $size={size} />
      </Wrapper>
    );
  }
);

export default Checkbox;
